<template>
  <div class="announcement-index p-3">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="h4 mb-0"><i class="fas fa-info-circle mr-2 text-primary"></i>{{ $t('dashboard-announcements.title-index') }}</h2>
      <b-button v-if="isSecretAdmin" variant="primary" @click="openPostModal">
        <i class="fas fa-plus mr-1"></i> {{ $t('dashboard-announcements.title-post') }}
      </b-button>
    </div>

    <div class="announcement-list">
      <div v-if="loading" class="text-center p-5">
        <b-spinner variant="primary"></b-spinner>
      </div>

      <div v-else-if="announcements.length === 0" class="text-center p-5 text-muted">
        {{ $t('dashboard-announcements.message-no-items') }}
      </div>

      <div v-else>
        <div
          v-for="item in announcements"
          :key="item.id"
          class="announcement-row"
          @click="viewDetail(item.id)"
        >
          <span
            class="unread-dot"
            :class="{ 'is-visible': !item.is_read }"
            :aria-label="!item.is_read ? $t('dashboard-announcements.label-unread') : ''"
          ></span>
          <span class="announcement-date">{{ formatDate(item.created_at) }}</span>
          <b-badge
            v-if="isNew(item.created_at)"
            variant="primary"
            pill
            class="announcement-badge"
          >New</b-badge>
          <span class="announcement-title">{{ item.title }}</span>

          <b-button
            v-if="isSecretAdmin"
            variant="outline-danger"
            size="sm"
            class="ml-auto announcement-delete-btn"
            :title="$t('dashboard-announcements.button-delete')"
            @click.stop="confirmDelete(item)"
          >
            <i class="fas fa-trash"></i>
          </b-button>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <announcement-post-modal ref="postModal" @posted="fetchAnnouncements" />
  </div>
</template>

<script>
import announcementApi from "@/api/announcements";
import AnnouncementPostModal from "../components/AnnouncementPostModal.vue";
import moment from "moment";
import { mapGetters } from "vuex";

export default {
  name: "AnnouncementIndex",
  components: {
    AnnouncementPostModal
  },
  data() {
    return {
      loading: false,
      announcements: [],
      isSecretAdmin: false
    };
  },
  computed: {
    ...mapGetters("dashboard", ["facility", "userData"])
  },
  watch: {
    userData: {
      immediate: true,
      handler() {
        this.checkSecretAdmin();
      }
    },
    facility: {
      immediate: true,
      handler() {
        this.checkSecretAdmin();
      }
    }
  },
  methods: {
    checkSecretAdmin() {
      // Logic from ExternalLinkSetting.vue
      this.isSecretAdmin = this.userData && this.facility && (this.userData.id === this.facility.user_id);
    },
    async fetchAnnouncements() {
      try {
        this.loading = true;
        const response = await announcementApi.getAnnouncements();
        this.announcements = response.data.data;
      } catch (error) {
        console.error("Failed to fetch announcements:", error);
      } finally {
        this.loading = false;
      }
    },
    openPostModal() {
      this.$refs.postModal.open();
    },
    viewDetail(id) {
      this.$router.push({ name: "dashboard-announcement-detail", params: { id } });
    },
    formatDate(date) {
      return moment(date).format("YYYY/M/D");
    },
    isNew(date) {
      return moment().diff(moment(date), 'days') <= 7;
    },
    async confirmDelete(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t("dashboard-announcements.confirm-delete-message", { title: item.title }),
        {
          title: this.$t("dashboard-announcements.confirm-delete-title"),
          okVariant: "danger",
          okTitle: this.$t("dashboard-announcements.button-delete"),
          cancelTitle: this.$t("dashboard-announcements.button-cancel"),
          centered: true
        }
      );
      if (!confirmed) return;

      try {
        await announcementApi.deleteAnnouncement(item.id);
        await this.fetchAnnouncements();
        // 削除されたお知らせが当該ユーザーで未読だった場合に備え、未読件数を同期しておく
        this.$store.dispatch("dashboard/fetchUnreadAnnouncementCount");
        this.$toast.success(this.$t("dashboard-announcements.message-deleted"));
      } catch (error) {
        console.error("Failed to delete announcement:", error);
        this.$toast.error(this.$t("dashboard-announcements.message-delete-failed"));
      }
    }
  },
  mounted() {
    this.fetchAnnouncements();
  }
};
</script>

<style scoped>
.announcement-list {
  border-top: 2px solid #5a67d8;
}
.announcement-row {
  display: flex;
  align-items: center;
  padding: 20px 16px;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background-color 0.15s;
}
.announcement-row:hover {
  background-color: #f7fafc;
}
/* 未読マーカー: 日付の左に固定幅で確保し、既読時はマーカー非表示でも位置揃えを維持する */
.unread-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
  background-color: transparent;
  flex: 0 0 8px;
}
.unread-dot.is-visible {
  background-color: #e53e3e;
}
.announcement-date {
  font-size: 1rem;
  font-weight: 600;
  color: #4a5568;
  white-space: nowrap;
  min-width: 110px;
}
.announcement-badge {
  font-size: 0.75rem;
  margin-right: 12px;
  padding: 3px 10px;
}
.announcement-title {
  font-size: 1.05rem;
  color: #2d3748;
}
.announcement-row:hover .announcement-title {
  color: #5a67d8;
}
.announcement-delete-btn {
  padding: 0.25rem 0.5rem;
}
</style>
